import { defineStore } from 'pinia'
import { authService } from '@/services/authService'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    accessToken: localStorage.getItem('access_token') || null,
    refreshToken: localStorage.getItem('refresh_token') || null,
    isAuthenticated: !!localStorage.getItem('access_token'),
    loading: false,
    error: null
  }),

  getters: {
    isLoggedIn: (state) => state.isAuthenticated && state.accessToken,
    userRole: (state) => state.user?.role || null,
    userName: (state) => state.user?.name || '',
    userEmail: (state) => state.user?.email || '',
    isAdmin: (state) => state.user?.role === 'admin',
    isTeacher: (state) => state.user?.role === 'teacher',
    isStudent: (state) => state.user?.role === 'student'
  },

  actions: {
    async register(userData) {
      this.loading = true
      this.error = null
      
      try {
        const response = await authService.register(userData)
        
        if (response.success) {
          const { user, access_token, refresh_token } = response.data
          
          this.user = user
          this.accessToken = access_token
          this.refreshToken = refresh_token
          this.isAuthenticated = true
          
          // Store in localStorage
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('refresh_token', refresh_token)
          localStorage.setItem('user', JSON.stringify(user))
          
          return { success: true, user }
        }
      } catch (error) {
        this.error = error.message || 'Registration failed'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async login(credentials) {
      this.loading = true
      this.error = null
      
      try {
        const response = await authService.login(credentials)
        
        if (response.success) {
          const { user, access_token, refresh_token } = response.data
          
          this.user = user
          this.accessToken = access_token
          this.refreshToken = refresh_token
          this.isAuthenticated = true
          
          // Store in localStorage
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('refresh_token', refresh_token)
          localStorage.setItem('user', JSON.stringify(user))
          
          return { success: true, user }
        }
      } catch (error) {
        this.error = error.message || 'Login failed'
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async fetchProfile() {
      if (!this.accessToken) return
      
      this.loading = true
      this.error = null
      
      try {
        const response = await authService.getProfile()
        
        if (response.success) {
          this.user = response.data.user
          localStorage.setItem('user', JSON.stringify(response.data.user))
          return { success: true, user: response.data.user }
        }
      } catch (error) {
        this.error = error.message || 'Failed to fetch profile'
        if (error.status === 401) {
          await this.refreshAccessToken()
        }
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async refreshAccessToken() {
      if (!this.refreshToken) {
        this.logout()
        return
      }
      
      try {
        const response = await authService.refreshToken()
        
        if (response.success) {
          const { access_token, refresh_token } = response.data
          
          this.accessToken = access_token
          this.refreshToken = refresh_token
          
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('refresh_token', refresh_token)
          
          return true
        }
      } catch (error) {
        this.logout()
        return false
      }
    },

    async logout() {
      try {
        if (this.accessToken) {
          await authService.logout()
        }
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        // Clear state and localStorage
        this.user = null
        this.accessToken = null
        this.refreshToken = null
        this.isAuthenticated = false
        this.error = null
        
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
      }
    },

    initializeAuth() {
      const token = localStorage.getItem('access_token')
      const user = localStorage.getItem('user')
      
      if (token && user) {
        this.accessToken = token
        this.refreshToken = localStorage.getItem('refresh_token')
        this.user = JSON.parse(user)
        this.isAuthenticated = true
      }
    },

    clearError() {
      this.error = null
    },

    getDashboardRoute() {
      if (!this.user) return '/login'
      return '/dashboard'
    }
  }
})